// Copyright (c) Docugami, Inc. All rights reserved.

import { EntityState, createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react'
import { Config } from 'config';
import { OrganizationSignalRConnectionInstance, waitForSignalRConnection } from 'networking/OrganizationSignalRConnection';
import { store } from 'store';
import { createDgBaseQuery } from 'store/baseQuery';
import { Box, TokenIdToBox } from 'store/shared_types';
import { SpanIdUtils } from 'utils/SpanIdUtils';
import { DgmlVersion, DgmlVersionSchema3, parseDgmlVersion } from 'utils/dgmldom';
import { createMappedTokensForEdits } from './dgmlSelectors';
import { Chunk, AnnotationsUpdatedMessage, ChunkStructureInfo, ChunkStructure, ChunkStyleInfo, Label, AddedChunkSchema } from './dgmlTypes';
import { Mutex } from 'async-mutex';
import { TokenKeyRange } from 'utils/MappedTokens';
import { PageInfo, TokenRange } from 'networking/api';
import { TokenType } from 'utils/TokensTextParser';
import { TokenRangeUtils } from 'utils/TokenRangeUtils';
import { startAppListening } from 'store/listenerMiddleware';
import { createFolderIdString } from 'components/file_management/file_helpers';
import { setPageCount } from 'store/documents/actions';

interface AnnotationNode {
    spanId: string;
    labels?: Label[];
    parent?: string;
    children?: string[];
    addedChunkValue?: string;
    colSpan?: number;
    rowSpan?: number;
    boundingBoxes?: Box[];
    structure?: ChunkStructure;
    structureAttribute?: string;
    styleAttribute?: string;
    outflowTokenRange?: TokenRange;
}

interface DgmlDeltaResponse<NodeType = AnnotationNode> {
    nodes: NodeType[];
    removed?: string[];
    edits?: TextEditOperation[]
}

export interface OutflowContainerInfo {
    // SpanId of the ouflow container
    id: string;
    // SpanId of the outflow - this is the child of the container
    outflowId: string;
    // SpanId of the root that this outflow belongs to. If this is a nested outflow this will be
    // equal to the outflowId on the parent OutflowContainerInfo
    rootId: string;
    // The page numbers containing this outflow
    pageNumbers: number[];
    // Flag indicating whether there are tokens in outflow
    isEmpty: boolean;
}

export interface DgmlData {
    version: DgmlVersionSchema3;
    chunks: EntityState<Chunk>;
    root: string;
    edits: TextEditOperation[];
    outflowContainers: EntityState<OutflowContainerInfo>;
}

export const outflowContainerInfoEntityAdapter = createEntityAdapter<OutflowContainerInfo>({
    selectId: x => x.id
});

export const {
    selectAll: selectAllOutflowContainers
} = outflowContainerInfoEntityAdapter.getSelectors();

export const chunkEntityAdapter = createEntityAdapter<Chunk>({
    selectId: x => x.spanId
});

export const {
    selectAll: selectAllChunks,
    selectById: selectChunkBySpanId,
} = chunkEntityAdapter.getSelectors();

const baseQuery = createDgBaseQuery(Config.dgmlUrl);
export const dgmlSlice = createApi({
    reducerPath: 'dgml',
    baseQuery: baseQuery,
    tagTypes: ['dgml', 'dgml-nodes', 'tokens', 'mapped-tokens', 'text', 'semantic-dgml', 'added-chunk-schema', 'token-locations', 'pages-info'],
    endpoints: builder => ({
        getDgml: builder.query<DgmlResourceResponse, DgmlResourceRequest>({
            query: (request: DgmlResourceRequest) => ({
                url: `/dgml-resources/${encodeURIComponent(request.docsetId)}/${encodeURIComponent(request.fileId)}/dgml`,
                params: createParams(request),
                responseHandler: async (response): Promise<DgmlResourceResponse> => ({
                    value: await response.text(),
                    version: response.headers.get("x-docugami-version")!
                })
            }),
            providesTags: (result, error, request) => [{ type: 'dgml', id: request.fileId }]
        }),
        getSemanticDgml: builder.query<DgmlResourceResponse, DgmlResourceRequest>({
            query: (request: DgmlResourceRequest) => ({
                url: `/dgml-resources/${encodeURIComponent(request.docsetId)}/${encodeURIComponent(request.fileId)}/semantic-dgml`,
                params: createParams(request),
                responseHandler: async (response): Promise<DgmlResourceResponse> => ({
                    value: await response.text(),
                    version: response.headers.get("x-docugami-version")!
                })
            }),
            providesTags: (result, error, request) => [{ type: 'semantic-dgml', id: request.fileId }]
        }),
        getDgmlNodes: builder.query<DgmlData, DgmlResourceRequest>({
            query: (request: DgmlResourceRequest) => ({
                url: `/dgml-resources/${encodeURIComponent(request.docsetId)}/${encodeURIComponent(request.fileId)}/dgml-delta`,
                params: createParams(request),
                responseHandler: async (response): Promise<DgmlResourceResponse<DgmlDeltaResponse>> => ({
                    value: await response.json(),
                    version: response.headers.get("x-docugami-version")!
                })
            }),
            keepUnusedDataFor: 30,
            providesTags: (result, error, request) => [{ type: 'dgml-nodes', id: request.fileId }],
            transformResponse: (result: DgmlResourceResponse<DgmlDeltaResponse>, meta, request) => {
                const dgmlVersion = parseDgmlVersion(result.version) as DgmlVersionSchema3;
                const chunks = result.value.nodes.map<Chunk>(x => parseAnnotationNode(request.docsetId, request.fileId, dgmlVersion, x, result.value.edits));
                const chunkState = chunkEntityAdapter.setAll(chunkEntityAdapter.getInitialState(), chunks);
                const data: DgmlData = {
                    version: dgmlVersion,
                    chunks: chunkState,
                    root: result.value.nodes.find(x => !x.parent)!.spanId,
                    edits: result.value.edits ?? [],
                    outflowContainers: outflowContainerInfoEntityAdapter.setAll(outflowContainerInfoEntityAdapter.getInitialState(), createOutflowContainerInfos(chunks, chunkState))
                }

                const addedChunksSchema = dgmlSlice.endpoints.getAddedChunkSchema.select({ docsetId: request.docsetId })(store.getState()).data;
                if (!!addedChunksSchema) {
                    // Create chunks from the definition that may not exist in the DGML
                    data.chunks = ensureAddedChunks(data.chunks, addedChunksSchema, request.docsetId, request.fileId);
                }

                return data;
            },
            onCacheEntryAdded: async (arg, api) => {
                await api.cacheDataLoaded;
                const mutex = new Mutex();

                const handleUpdate = async (payload: AnnotationsUpdatedMessage) => {
                    if (payload.fileId !== arg.fileId) {
                        return;
                    }

                    const checkVersion = (): DgmlVersion | undefined => {
                        let version = api.getCacheEntry().data?.version;
                        if (!version) {
                            return;
                        }

                        if (version.annotationEventId.localeCompare(payload.annotationEventId) >= 0) {
                            // We have a newer version already
                            return;
                        }

                        return version;
                    }

                    if (!checkVersion()) {
                        return;
                    }

                    await mutex.waitForUnlock();

                    const version = checkVersion();
                    if (!version) {
                        return;
                    }

                    const release = await mutex.acquire()

                    try {
                        const response = await api.dispatch(dgmlSlice.endpoints.getDgmlNodesDelta.initiate({
                            version: version.value,
                            docsetId: arg.docsetId,
                            fileId: arg.fileId
                        })).unwrap();

                        api.updateCachedData(x => {
                            const newDgmlVersion = parseDgmlVersion(response.version) as DgmlVersionSchema3;
                            const textVersionChanged = newDgmlVersion.textEventStreamVersion !== x.version.textEventStreamVersion;
                            x.version = newDgmlVersion;
                            if (!!response.value.edits?.length) {
                                x.edits.push(...response.value.edits);
                            }

                            if (!!response.value.removed?.length) {
                                chunkEntityAdapter.removeMany(x.chunks, response.value.removed);
                                outflowContainerInfoEntityAdapter.removeMany(x.outflowContainers, response.value.removed);
                            }

                            if (textVersionChanged) {
                                const mappedTokens = selectMappedTokens(arg.fileId, x.version, x.edits);
                                const updated: Chunk[] = [];
                                for (const chunk of selectAllChunks(x.chunks)) {
                                    const tokenRange = mappedTokens.tokenKeyRangeToTokenRange(chunk.tokenKeyRange);
                                    if (!tokenRange) {
                                        throw new Error("Failed to parse token range for DGML node");
                                    }

                                    if (!TokenRangeUtils.rangesEqual(chunk.tokenRange, tokenRange)) {
                                        updated.push({
                                            ...chunk,
                                            tokenRange: tokenRange
                                        })
                                    }
                                }

                                if (!!updated.length) {
                                    chunkEntityAdapter.upsertMany(x.chunks, updated);
                                }
                            }

                            const modifiedChunks = response.value.nodes.map<Chunk>(n => parseAnnotationNode(arg.docsetId, arg.fileId, x.version, n, x.edits));
                            chunkEntityAdapter.upsertMany(x.chunks, modifiedChunks);

                            const rootIndex = modifiedChunks.findIndex(x => !x.parent);
                            if (rootIndex !== -1) {
                                x.root = modifiedChunks[rootIndex].spanId;
                            }

                            const outflowContainerInfos = createOutflowContainerInfos(modifiedChunks, x.chunks);
                            outflowContainerInfoEntityAdapter.upsertMany(x.outflowContainers, outflowContainerInfos);
                        })
                    }
                    catch (error) {
                        api.dispatch(dgmlSlice.util.invalidateTags([{ type: "dgml-nodes", id: arg.fileId }]))
                    }
                    finally {
                        release();
                    }
                }

                const signalRInstance = await waitForSignalRConnection();
                signalRInstance.connection.on("annotationsUpdated", handleUpdate);
                try {
                    await api.cacheEntryRemoved;
                }
                finally {
                    OrganizationSignalRConnectionInstance?.connection.off("annotationsUpdated", handleUpdate);
                }
            },
        }),
        getDgmlNodesDelta: builder.mutation<DgmlResourceResponse<DgmlDeltaResponse>, DgmlResourceRequest>({
            query: (request: DgmlResourceRequest) => ({
                url: `/dgml-resources/${encodeURIComponent(request.docsetId)}/${encodeURIComponent(request.fileId)}/dgml-delta`,
                params: createParams(request),
                responseHandler: async (response): Promise<DgmlResourceResponse<DgmlDeltaResponse>> => ({
                    value: await response.json(),
                    version: response.headers.get("x-docugami-version")!
                })
            })
        }),
        getStartText: builder.query<DgmlResourceResponse, DgmlResourceRequest>({
            query: (request: DgmlResourceRequest) => ({
                url: `/dgml-resources/${encodeURIComponent(request.docsetId)}/${encodeURIComponent(request.fileId)}/start-text`,
                params: createParams(request),
                responseHandler: async (response): Promise<DgmlResourceResponse> => ({
                    value: await response.text(),
                    version: response.headers.get("x-docugami-version")!
                })
            }),
            providesTags: (result, error, request) => [{ type: 'text', id: request.fileId }]
        }),
        getMappedTokens: builder.query<DgmlResourceResponse, DgmlResourceRequest>({
            query: (request: DgmlResourceRequest) => ({
                url: `/dgml-resources/${encodeURIComponent(request.docsetId)}/${encodeURIComponent(request.fileId)}/mapped-tokens`,
                params: createParams(request),
                responseHandler: async (response): Promise<DgmlResourceResponse> => ({
                    value: response.status === 204 ? '' : await response.text(),
                    version: response.headers.get("x-docugami-version")!
                })
            }),
            providesTags: (result, error, request) => [{ type: 'mapped-tokens', id: request.fileId }],
        }),
        getTokens: builder.query<DgmlResourceResponse, DgmlResourceRequest>({
            query: (request: DgmlResourceRequest) => ({
                url: `/dgml-resources/${encodeURIComponent(request.docsetId)}/${encodeURIComponent(request.fileId)}/tokens`,
                params: createParams(request),
                responseHandler: async (response): Promise<DgmlResourceResponse> => ({
                    value: await response.text(),
                    version: response.headers.get("x-docugami-version")!
                })
            }),
            providesTags: (result, error, request) => [{ type: 'tokens', id: request.fileId }]
        }),
        getTokenLocations: builder.query<DgmlResourceResponse<TokenIdToBox>, DgmlPageResourceRequest>({
            query: (request: DgmlPageResourceRequest) => ({
                url: `/dgml-resources/${encodeURIComponent(request.docsetId)}/${encodeURIComponent(request.fileId)}/${encodeURIComponent(request.page)}/token-locations`,
                params: createParams(request),
                responseHandler: async (response): Promise<DgmlResourceResponse<TokenIdToBox>> => ({
                    value: response.status === 404 ? {} : await response.json(),
                    version: response.headers.get("x-docugami-version")!
                })
            }),
            providesTags: (result, error, request) => [{ type: 'token-locations', id: `${request.fileId}.${request.page}` }]
        }),
        getPagesInfo: builder.query<DgmlResourceResponse<PageInfo[]>, DgmlResourceRequest>({
            query: (request: DgmlPageResourceRequest) => ({
                url: `/dgml-resources/${encodeURIComponent(request.docsetId)}/${encodeURIComponent(request.fileId)}/pages-info`,
                params: createParams(request),
                responseHandler: async (response): Promise<DgmlResourceResponse> => ({
                    value: await response.json(),
                    version: response.headers.get("x-docugami-version")!
                })
            }),
            transformResponse: (result: DgmlResourceResponse<PageInfo[]>, meta, request): DgmlResourceResponse<PageInfo[]> => {
                return {
                    ...result,
                    value: result.value.map(x => {
                        if (!!x.tokenRanges) {
                            x.tokenRanges.forEach(range => range.toTok++);
                        }
                        else if (x.fromTok !== undefined && x.toTok !== undefined && x.fromTok !== -1 && x.toTok !== -1) {
                            x.tokenRanges = [{fromTok: x.fromTok, toTok: x.toTok + 1}]
                        }
                        else {
                            x.tokenRanges = [];
                        }

                        return x;
                    })
                }
            },
            providesTags: (result, error, request) => [{ type: 'pages-info', id: request.fileId }]
        }),
        getAddedChunkSchema: builder.query<AddedChunkSchema, DgmlDocsetResourceRequest>({
            query: (request: DgmlResourceRequest) => ({
                url: `/dgml-resources/${encodeURIComponent(request.docsetId)}/added-chunk-schema`,
                params: createDocsetParams(request)
            }),
            providesTags: (result, error, request) => [{ type: 'added-chunk-schema', id: request.docsetId }]
        })
    })
});

export const {
    useGetDgmlQuery,
    useGetSemanticDgmlQuery,
    useGetMappedTokensQuery,
    useGetStartTextQuery,
    useGetTokensQuery,
    useGetDgmlNodesQuery,
    useGetAddedChunkSchemaQuery,
    useGetPagesInfoQuery,
    useGetTokenLocationsQuery
} = dgmlSlice;

export interface GetTokenLocationsRequest {
    fileId: string;
    docsetId: string;
    page: number;
    version: string
}

export interface DgmlResourceRequest {
    fileId: string;
    docsetId: string;
    version?: string;
    includeText?: boolean;
}

export interface DgmlPageResourceRequest {
    fileId: string;
    docsetId: string;
    page: number;
    version?: string;
}

export interface DgmlDocsetResourceRequest {
    docsetId: string;
    version?: string;
}

export interface DgmlResourceResponse<T = string> {
    value: T;
    version: string;
}

export interface GetEditOperationsRequest {
    docsetId: string;
    fileId: string;
}

export interface GetEditOperationsDeltaRequest {
    docsetId: string;
    fileId: string;
    version: string;
}

export interface GetTextEditOperationsResponse {
    edits: TextEditOperation[];
    version: string;
}

export interface TextEditOperation {
    tokenKeyRange: TokenKeyRange,
    tokenRange: TokenRange,
    text: string;
    removedTokens: EditToken[];
    addedTokens: EditToken[];
    tokenCountDelta: number;
    totalTokenCount: number;
    streamVersion: number;
}

export interface EditToken {
    fromChar: number;
    toChar: number;
    type: TokenType;
    text: string;
    index: number;
    key: string;
}

function createParams(request: DgmlResourceRequest): Record<string, any> | undefined {
    let params = createDocsetParams(request);
    if (!!request.includeText) {
        params = params ?? {};
        params['includeText'] = true;
    }

    return params;
}

function createDocsetParams(request: DgmlDocsetResourceRequest): Record<string, any> | undefined {
    let params: Record<string, any> | undefined = undefined;
    if (!!request.version) {
        params = params ?? {};
        params['version'] = request.version
    }

    return params;
}

const selectMappedTokens = createSelector(
    [
        (fileId: string, dgmlVersion: DgmlVersion, edits: TextEditOperation[] | undefined) => fileId,
        (fileId: string, dgmlVersion: DgmlVersion, edits: TextEditOperation[] | undefined) => dgmlVersion.textEventStreamVersion,
        (fileId: string, dgmlVersion: DgmlVersion, edits: TextEditOperation[] | undefined) => edits
    ],
    (fileId, textVersion, edits) => createMappedTokensForEdits(edits)
);

const parseAnnotationNode = (docsetId: string, fileId: string, dgmlVersion: DgmlVersion, node: AnnotationNode, edits?: TextEditOperation[]): Chunk => {
    const mappedTokens = selectMappedTokens(fileId, dgmlVersion, edits);
    if (!mappedTokens) {
        throw new Error("Dgml should not be fetched before mapped tokens");
    }

    const spanId = SpanIdUtils.parse(node.spanId);
    const tokenKeyRange = { fromTokenKey: spanId.fromTokenKey, toTokenKey: spanId.toTokenKey }
    const tokenRange = mappedTokens.tokenKeyRangeToTokenRange(tokenKeyRange);
    if (!tokenRange) {
        throw new Error("Failed to parse token range for DGML node");
    }

    const chunkStructure: ChunkStructureInfo | undefined = !!node.structure ? {
        value: node.structureAttribute!,
        type: node.structure,
        rowspan: node.rowSpan,
        colspan: node.colSpan
    } : undefined;

    const chunkStyle: ChunkStyleInfo | undefined = !!node.styleAttribute || !!node.boundingBoxes ? {
        value: node.styleAttribute ?? '',
        boundingBoxes: node.boundingBoxes
    } : undefined;

    return {
        spanId: node.spanId,
        tokenKeyRange: tokenKeyRange,
        labels: node.labels ?? [],
        parent: node.parent,
        children: node.children ?? [],
        isRoot: !node.parent,
        structure: chunkStructure,
        style: chunkStyle,
        tokenRange: tokenRange,
        docsetId: docsetId,
        fileId: fileId,
        outflowTokenRange: node.outflowTokenRange,
        addedChunkValue: node.addedChunkValue
    }
}

const ensureAddedChunks = (state: EntityState<Chunk>, schema: AddedChunkSchema, docsetId: string, fileId: string) => {
    for (const definition of schema.definitions) {
        const spanId = SpanIdUtils.createAddedChunk(fileId, definition.id);
        const chunk = state.entities[spanId.value];
        if (!chunk) {
            state = chunkEntityAdapter.addOne(state, {
                docsetId: docsetId,
                fileId: fileId,
                spanId: spanId.value,
                tokenKeyRange: { fromTokenKey: spanId.fromTokenKey, toTokenKey: spanId.toTokenKey },
                labels: [],
                parent: SpanIdUtils.createAddedChunk(fileId).value,
                tokenRange: { fromTok: 0, toTok: 0 },
                children: []
            })
        }
    }

    return state;
}

const createOutflowContainerInfos = (chunks: Chunk[], state: EntityState<Chunk>): OutflowContainerInfo[] => {
    const containerInfos: OutflowContainerInfo[] = [];
    for (const chunk of chunks) {
        if (chunk.structure?.type !== ChunkStructure.OutflowContainer) {
            continue;
        }

        if (!chunk.parent) {
            throw new Error(`Outflow without a parent spanId: ${chunk.spanId}`);
        }

        let containerRootId: string | undefined;
        let current: Chunk | undefined = state.entities[chunk.parent];
        while (!containerRootId && !!current) {
            let newParent = state.entities[current.parent ?? ''];
            if (!newParent || newParent.structure?.type === ChunkStructure.OutflowContainer) {
                containerRootId = current.spanId;
            }
            else {
                current = newParent;
            }
        }

        if (!containerRootId) {
            throw new Error(`Could not find a valid root containing this outflow. SpanId: ${chunk.spanId}`);
        }

        containerInfos.push({
            id: chunk.spanId,
            outflowId: chunk.children[0],
            rootId: containerRootId,
            pageNumbers: Array.from(new Set(chunk.style?.boundingBoxes?.map(x => x.page) ?? [])).sort((a, b) => a - b),
            isEmpty: !!chunk.outflowTokenRange && TokenRangeUtils.rangeIsEmpty(chunk.outflowTokenRange)
        })
    }

    return containerInfos;
}

startAppListening({
    matcher: dgmlSlice.endpoints.getPagesInfo.matchFulfilled,
    effect: (input, listenerApi) => {
        const originalRequest = input.meta.arg.originalArgs;
        const clusterDocument = listenerApi.getState().documents.indexed[createFolderIdString(originalRequest.docsetId, originalRequest.fileId)]?.clusterDocument;
        const pageCount = input.payload.value.length;
        if (!!clusterDocument && clusterDocument.numberOfPages !== pageCount) {
            listenerApi.dispatch(setPageCount({
                docId: clusterDocument.folderId,
                value: pageCount
            }));
        }
    }
})
